// src/services/kanbanService.js

import axios from 'axios';

// Construir la base URL correctamente para Kanban
// La API de Kanban está en /api/kanban/, no en /api/content/
const getKanbanBaseURL = () => {
    if (process.env.NODE_ENV === 'production') {
        return 'https://agencia.geves.cl/api';
    }
    return 'http://localhost:8000/api';
};

const api = axios.create({
    baseURL: getKanbanBaseURL(),
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    }
});

// Función para obtener CSRF token
function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

// Interceptor para añadir CSRF token a peticiones POST/PUT/DELETE
api.interceptors.request.use((config) => {
    if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
        config.headers['X-CSRFToken'] = getCookie('csrftoken');
    }
    return config;
});

// Interceptor para manejar errores
api.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            window.location.href = '/ingresar/';
        }
        return Promise.reject(error);
    }
);

const kanbanService = {
    // ============================================
    // BOARDS (Tableros)
    // ============================================

    /**
     * Obtener MIS tableros (donde soy propietario)
     * GET /api/kanban/boards/my_boards/
     */
    getMyBoards: async () => {
        const response = await api.get('/kanban/boards/my_boards/');
        return response.data;
    },

    /**
     * Obtener tableros compartidos conmigo
     * GET /api/kanban/boards/shared_with_me/
     */
    getSharedBoards: async () => {
        const response = await api.get('/kanban/boards/shared_with_me/');
        return response.data;
    },

    /**
     * Obtener todos mis tableros (propios + compartidos)
     * GET /api/kanban/boards/
     */
    getAllBoards: async (params = {}) => {
        const response = await api.get('/kanban/boards/', { params });
        return response.data;
    },

    /**
     * Obtener detalle de un tablero
     * GET /api/kanban/boards/{id}/
     */
    getBoard: async (boardId) => {
        const response = await api.get(`/kanban/boards/${boardId}/`);
        return response.data;
    },

    /**
     * Crear tablero
     * POST /api/kanban/boards/
     */
    createBoard: async (boardData) => {
        const response = await api.post('/kanban/boards/', boardData);
        return response.data;
    },

    /**
     * Actualizar tablero
     * PUT /api/kanban/boards/{id}/
     */
    updateBoard: async (boardId, boardData) => {
        const response = await api.put(`/kanban/boards/${boardId}/`, boardData);
        return response.data;
    },

    /**
     * Eliminar tablero
     * DELETE /api/kanban/boards/{id}/
     */
    deleteBoard: async (boardId) => {
        const response = await api.delete(`/kanban/boards/${boardId}/`);
        return response.data;
    },

    /**
     * Archivar/Desarchivar tablero
     * POST /api/kanban/boards/{id}/archive/
     */
    toggleArchiveBoard: async (boardId) => {
        const response = await api.post(`/kanban/boards/${boardId}/archive/`);
        return response.data;
    },

    /**
     * Duplicar tablero
     * POST /api/kanban/boards/{id}/duplicate/
     */
    duplicateBoard: async (boardId) => {
        const response = await api.post(`/kanban/boards/${boardId}/duplicate/`);
        return response.data;
    },

    // ============================================
    // BOARD STATUSES (Estados)
    // ============================================

    /**
     * Obtener estados de un tablero
     * GET /api/kanban/boards/{boardId}/statuses/
     */
    getBoardStatuses: async (boardId) => {
        const response = await api.get(`/kanban/boards/${boardId}/statuses/`);
        return response.data;
    },

    /**
     * Crear estado
     * POST /api/kanban/boards/{boardId}/statuses/
     */
    createStatus: async (boardId, statusData) => {
        const response = await api.post(`/kanban/boards/${boardId}/statuses/`, statusData);
        return response.data;
    },

    /**
     * Actualizar estado
     * PUT /api/kanban/boards/{boardId}/statuses/{statusId}/
     */
    updateStatus: async (boardId, statusId, statusData) => {
        const response = await api.put(`/kanban/boards/${boardId}/statuses/${statusId}/`, statusData);
        return response.data;
    },

    /**
     * Eliminar estado
     * DELETE /api/kanban/boards/{boardId}/statuses/{statusId}/
     */
    deleteStatus: async (boardId, statusId) => {
        const response = await api.delete(`/kanban/boards/${boardId}/statuses/${statusId}/`);
        return response.data;
    },

    /**
     * Reordenar estados
     * POST /api/kanban/boards/{boardId}/statuses/reorder/
     */
    reorderStatuses: async (boardId, ordenData) => {
        const response = await api.post(`/kanban/boards/${boardId}/statuses/reorder/`, ordenData);
        return response.data;
    },

    // ============================================
    // BOARD MEMBERS (Miembros)
    // ============================================

    /**
     * Obtener miembros de un tablero
     * GET /api/kanban/boards/{boardId}/members/
     */
    getBoardMembers: async (boardId) => {
        const response = await api.get(`/kanban/boards/${boardId}/members/`);
        return response.data;
    },

    /**
     * Actualizar rol de miembro
     * PUT /api/kanban/boards/{boardId}/members/{memberId}/
     */
    updateMemberRole: async (boardId, memberId, roleData) => {
        const response = await api.put(`/kanban/boards/${boardId}/members/${memberId}/`, roleData);
        return response.data;
    },

    /**
     * Remover miembro
     * DELETE /api/kanban/boards/{boardId}/members/{memberId}/
     */
    removeMember: async (boardId, memberId) => {
        const response = await api.delete(`/kanban/boards/${boardId}/members/${memberId}/`);
        return response.data;
    },

    /**
     * Salir del tablero
     * POST /api/kanban/boards/{boardId}/members/leave/
     */
    leaveBoard: async (boardId) => {
        const response = await api.post(`/kanban/boards/${boardId}/members/leave/`);
        return response.data;
    },

    // ============================================
    // INVITATIONS (Invitaciones)
    // ============================================

    /**
     * Obtener mis invitaciones recibidas
     * GET /api/kanban/invitations/
     */
    getMyInvitations: async () => {
        const response = await api.get('/kanban/invitations/');
        return response.data;
    },

    /**
     * Obtener invitaciones de un tablero
     * GET /api/kanban/boards/{boardId}/invitations/
     */
    getBoardInvitations: async (boardId) => {
        const response = await api.get(`/kanban/boards/${boardId}/invitations/`);
        return response.data;
    },

    /**
     * Crear invitación
     * POST /api/kanban/boards/{boardId}/invitations/
     */
    createInvitation: async (boardId, invitationData) => {
        const response = await api.post(`/kanban/boards/${boardId}/invitations/`, invitationData);
        return response.data;
    },

    /**
     * Aceptar invitación
     * POST /api/kanban/invitations/{token}/accept/
     */
    acceptInvitation: async (token) => {
        const response = await api.post(`/kanban/invitations/${token}/accept/`);
        return response.data;
    },

    /**
     * Rechazar invitación
     * POST /api/kanban/invitations/{token}/decline/
     */
    declineInvitation: async (token) => {
        const response = await api.post(`/kanban/invitations/${token}/decline/`);
        return response.data;
    },

    /**
     * Cancelar invitación
     * DELETE /api/kanban/invitations/{id}/
     */
    cancelInvitation: async (invitationId) => {
        const response = await api.delete(`/kanban/invitations/${invitationId}/`);
        return response.data;
    },

    // ============================================
    // TASKS (Tareas)
    // ============================================

    /**
     * Obtener todas las tareas con filtros opcionales
     * GET /api/kanban/tasks/
     */
    getAllTasks: async (params = {}) => {
        const response = await api.get('/kanban/tasks/', { params });
        return response.data;
    },

    /**
     * Obtener tareas de un tablero
     * GET /api/kanban/boards/{boardId}/tasks/
     */
    getBoardTasks: async (boardId, params = {}) => {
        const response = await api.get(`/kanban/boards/${boardId}/tasks/`, { params });
        return response.data;
    },

    /**
     * Obtener mis tareas asignadas
     * GET /api/kanban/boards/{boardId}/tasks/my_tasks/
     */
    getMyTasks: async (boardId) => {
        const response = await api.get(`/kanban/boards/${boardId}/tasks/my_tasks/`);
        return response.data;
    },

    /**
     * Obtener detalle de tarea
     * GET /api/kanban/tasks/{taskId}/
     */
    getTask: async (taskId) => {
        const response = await api.get(`/kanban/tasks/${taskId}/`);
        return response.data;
    },

    /**
     * Crear tarea
     * POST /api/kanban/boards/{boardId}/tasks/
     */
    createTask: async (boardId, taskData) => {
        const response = await api.post(`/kanban/boards/${boardId}/tasks/`, taskData);
        return response.data;
    },

    /**
     * Actualizar tarea
     * PUT /api/kanban/tasks/{taskId}/
     */
    updateTask: async (taskId, taskData) => {
        const response = await api.put(`/kanban/tasks/${taskId}/`, taskData);
        return response.data;
    },

    /**
     * Eliminar tarea
     * DELETE /api/kanban/tasks/{taskId}/
     */
    deleteTask: async (taskId) => {
        const response = await api.delete(`/kanban/tasks/${taskId}/`);
        return response.data;
    },

    /**
     * Mover tarea a otro estado
     * POST /api/kanban/tasks/{taskId}/move/
     */
    moveTask: async (taskId, statusId) => {
        const response = await api.post(`/kanban/tasks/${taskId}/move/`, { status_id: statusId });
        return response.data;
    },

    /**
     * Iniciar cronómetro
     * POST /api/kanban/tasks/{taskId}/start_timer/
     */
    startTimer: async (taskId) => {
        const response = await api.post(`/kanban/tasks/${taskId}/start_timer/`);
        return response.data;
    },

    /**
     * Detener cronómetro
     * POST /api/kanban/tasks/{taskId}/stop_timer/
     */
    stopTimer: async (taskId) => {
        const response = await api.post(`/kanban/tasks/${taskId}/stop_timer/`);
        return response.data;
    },

    // ============================================
    // TIME LOGS
    // ============================================

    /**
     * Obtener time logs
     * GET /api/kanban/time-logs/
     */
    getTimeLogs: async (params = {}) => {
        const response = await api.get('/kanban/time-logs/', { params });
        return response.data;
    },

    /**
     * Crear registro de tiempo manual
     * POST /api/kanban/time-logs/
     */
    createManualTimeLog: async (data) => {
        const response = await api.post('/kanban/time-logs/', data);
        return response.data;
    },

    // ============================================
    // ALIAS para compatibilidad con componentes
    // ============================================
    archiveBoard: async (boardId) => {
        return kanbanService.toggleArchiveBoard(boardId);
    },

    getStatuses: async (boardId) => {
        return kanbanService.getBoardStatuses(boardId);
    },

    getMembers: async (boardId) => {
        return kanbanService.getBoardMembers(boardId);
    },

    getTasks: async (boardId, params = {}) => {
        return kanbanService.getBoardTasks(boardId, params);
    },

    sendInvitation: async (boardId, invitationData) => {
        return kanbanService.createInvitation(boardId, invitationData);
    },

    getTableros: async () => {
        return kanbanService.getAllBoards();
    },
};

export default kanbanService;